import { Controller } from '@hotwired/stimulus';

/**
 * Address Autocomplete Controller
 *
 * BAN (Base Adresse Nationale) autocomplete for address inputs. Runs in two
 * mutually exclusive modes selected by the `single` value:
 *
 * - MULTI mode (default, `single` false) — the legacy 11-site behavior: a
 *   picked suggestion is decomposed into the line1/postalCode/city/country
 *   targets, each highlighted, and focus jumps to the complement field. Bound
 *   on `click`. Untouched by the single-mode additions below.
 * - SINGLE mode (`single` true) — one free-text input (the `line1` target)
 *   holding the WHOLE BAN label. Used by the two Compte rendu location fields
 *   (« Lieu » / « Lieu de la prochaine réunion »), which persist through the
 *   `cr-inline-field` inline-PATCH protocol.
 *
 * Single-mode save contract (why mousedown + native blur, not synthetic events):
 * - Suggestion items bind `mousedown->address-autocomplete#select`. `mousedown`
 *   fires BEFORE the input's `blur`, and `select` calls `preventDefault()` so the
 *   input never loses focus at pick time — this kills the race where the input's
 *   own `blur->cr-inline-field#patchField` would PATCH the half-typed text.
 * - After writing the full label, `select` calls the NATIVE `line1Target.blur()`.
 *   That single real blur fires the input's own `blur->cr-inline-field#patchField`
 *   data-action exactly once with the full label, releases focus (no redundant
 *   second PATCH, mobile keyboard closes). We never dispatch synthetic
 *   `Event('change')`/`Event('blur')` — Stimulus binds at-target, one blur suffices.
 *
 * Stale-fetch guard (single mode): a monotonic `generation` counter is bumped on
 * every search, on every pick, and on every close (`hideSuggestions`).
 * `fetchAddresses` snapshots it and drops its result if the generation moved while
 * the request was in flight, so a fetch that resolves after a pick OR after a close
 * (Escape / focusout) can NEVER reopen the dropdown — the close itself invalidates
 * any in-flight request, not just a queued debounce.
 *
 * Close behaviors (single mode): the dropdown hides on the input's `focusout`
 * (safe — mousedown selection runs first and `preventDefault` keeps focus) and on
 * `Escape`; `hideSuggestions` clears the pending debounce and bumps the generation.
 *
 * Keyboard map (single mode): ArrowDown/ArrowUp move the active `option`
 * highlight, Enter picks the active option (same path as a mouse `select`),
 * Escape closes. Bound programmatically on the input in `connect` and removed in
 * `disconnect` (connect/disconnect symmetry).
 *
 * Targets:
 * - suggestions: The dropdown container (role="listbox" in single mode via markup).
 * - line1: In single mode, the free-text input holding the full BAN label.
 * - line2/postalCode/city/country: Multi-mode decomposed address fields.
 *
 * Values:
 * - minLength: Minimum query length before querying the BAN API (default 3).
 * - limit: Maximum number of suggestions requested (default 5).
 * - single: Opt-in single-field mode (default false — legacy sites unchanged).
 *
 * Actions:
 * - search: Debounced BAN lookup on input.
 * - select: Fill the field(s) from the chosen suggestion.
 *
 * @example
 * <div data-controller="address-autocomplete" data-address-autocomplete-single-value="true">
 *   <input data-address-autocomplete-target="line1"
 *          data-action="input->address-autocomplete#search blur->cr-inline-field#patchField">
 *   <div data-address-autocomplete-target="suggestions" role="listbox" class="hidden"></div>
 * </div>
 */
export default class extends Controller {
  static targets = ['suggestions', 'line1', 'line2', 'postalCode', 'city', 'country'];
  static values = {
    minLength: { type: Number, default: 3 },
    limit: { type: Number, default: 5 },
    single: { type: Boolean, default: false },
  };

  connect() {
    this.timeout = null;
    this.highlightTimeout = null;
    this.generation = 0;

    if (this.singleValue && this.hasLine1Target) {
      this.hideOnFocusOutHandler = this.#hideOnFocusOut.bind(this);
      this.keydownHandler = this.#handleKeydown.bind(this);
      this.line1Target.addEventListener('focusout', this.hideOnFocusOutHandler);
      this.line1Target.addEventListener('keydown', this.keydownHandler);
    }
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    if (this.highlightTimeout) {
      clearTimeout(this.highlightTimeout);
    }

    if (this.singleValue && this.hasLine1Target) {
      this.line1Target.removeEventListener('focusout', this.hideOnFocusOutHandler);
      this.line1Target.removeEventListener('keydown', this.keydownHandler);
    }
  }

  // Called when user types in the search field
  search(event) {
    // Bump the generation on every search so an in-flight fetch tied to an older
    // generation is dropped by the single-mode stale-fetch guard.
    this.generation += 1;

    const query = event.target.value.trim();

    if (query.length < this.minLengthValue) {
      this.hideSuggestions();
      return;
    }

    // Debounce API calls (wait 300ms after user stops typing)
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.timeout = setTimeout(() => {
      this.fetchAddresses(query);
    }, 300);
  }

  // Fetch addresses from French government API
  async fetchAddresses(query) {
    const generation = this.generation;
    const url = `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=${this.limitValue}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      // Stale-fetch guard (single mode): a pick or newer search bumped the
      // generation while this request was in flight — drop it so a resolved
      // fetch can never reopen the dropdown after the user already selected.
      if (this.singleValue && generation !== this.generation) return;

      if (data.features && data.features.length > 0) {
        this.displaySuggestions(data.features);
      } else {
        this.hideSuggestions();
      }
    } catch (error) {
      if (this.singleValue && generation !== this.generation) return;
      console.error('Error fetching addresses:', error);
      this.hideSuggestions();
    }
  }

  // Build a single suggestion DOM element
  buildSuggestionItem(props) {
    if (this.singleValue) {
      return this.#buildSingleSuggestionItem(props);
    }

    const item = document.createElement('div');
    item.className =
      'cursor-pointer p-3 hover:bg-primary-50 border-b border-neutral-100 last:border-0 transition-colors';
    item.setAttribute('data-action', 'click->address-autocomplete#select');
    item.setAttribute(
      'data-address',
      JSON.stringify({
        line1: props.name,
        postal_code: props.postcode,
        city: props.city,
        country: 'France',
      })
    );

    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-start';

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'h-4 w-4 text-neutral-400 mr-3 mt-0.5');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z'
    );
    icon.appendChild(path);

    const content = document.createElement('div');
    content.className = 'flex-1';

    const nameEl = document.createElement('div');
    nameEl.className = 'text-sm font-medium text-neutral-900';
    nameEl.textContent = props.name;

    const detailEl = document.createElement('div');
    detailEl.className = 'text-xs text-neutral-500';
    detailEl.textContent = `${props.postcode} ${props.city}`;

    content.appendChild(nameEl);
    content.appendChild(detailEl);
    wrapper.appendChild(icon);
    wrapper.appendChild(content);
    item.appendChild(wrapper);

    return item;
  }

  // Build a single-mode suggestion element carrying the full BAN label. Bound on
  // mousedown (not click) so the pick runs before the input blurs; role="option"
  // for accessibility + keyboard navigation. No innerHTML — textContent only.
  #buildSingleSuggestionItem(props) {
    const item = document.createElement('div');
    item.className =
      'cursor-pointer p-3 hover:bg-primary-50 border-b border-neutral-100 last:border-0 transition-colors';
    item.setAttribute('role', 'option');
    item.setAttribute('data-action', 'mousedown->address-autocomplete#select');
    item.setAttribute('data-address', JSON.stringify({ label: props.label }));

    const wrapper = document.createElement('div');
    wrapper.className = 'flex items-start';

    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('class', 'h-4 w-4 text-neutral-400 mr-3 mt-0.5');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('viewBox', '0 0 24 24');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    path.setAttribute('stroke-width', '2');
    path.setAttribute(
      'd',
      'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z'
    );
    icon.appendChild(path);

    const label = document.createElement('div');
    label.className = 'flex-1 text-sm text-neutral-900';
    label.textContent = props.label;

    wrapper.appendChild(icon);
    wrapper.appendChild(label);
    item.appendChild(wrapper);

    return item;
  }

  // Display suggestion list
  displaySuggestions(features) {
    this.suggestionsTarget.replaceChildren();

    features.forEach((feature) => {
      this.suggestionsTarget.appendChild(this.buildSuggestionItem(feature.properties));
    });

    this.suggestionsTarget.classList.remove('hidden');
  }

  // Hide suggestion list
  hideSuggestions() {
    // Single mode: cancel any pending debounce AND bump the generation so neither a
    // queued fetch nor one ALREADY IN FLIGHT can reopen the dropdown after a close
    // (Escape / focusout / pick). The in-flight fetch snapshots the generation and is
    // dropped by the stale-fetch guard once this bump moves it. Multi mode is untouched.
    if (this.singleValue) {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }
      this.generation += 1;
    }

    this.suggestionsTarget.classList.add('hidden');
    this.suggestionsTarget.replaceChildren();
  }

  // Select an address from suggestions
  select(event) {
    if (this.singleValue) {
      // mousedown → the input never blurs here, killing the partial-text PATCH race.
      event.preventDefault();
      this.#selectSingle(event.currentTarget);
      return;
    }

    const address = JSON.parse(event.currentTarget.dataset.address);

    // Array to track which fields were auto-filled
    const filledFields = [];

    // Fill in the form fields and track them
    if (this.hasLine1Target) {
      this.line1Target.value = address.line1;
      filledFields.push(this.line1Target);
    }
    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.value = address.postal_code;
      filledFields.push(this.postalCodeTarget);
    }
    if (this.hasCityTarget) {
      this.cityTarget.value = address.city;
      filledFields.push(this.cityTarget);
    }
    if (this.hasCountryTarget) {
      this.countryTarget.value = address.country || 'France';
      filledFields.push(this.countryTarget);
    }

    // Hide suggestions
    this.hideSuggestions();

    // Apply highlight effect to auto-filled fields
    this.highlightFields(filledFields);

    // Focus on line2 (complement) for optional details
    if (this.hasLine2Target) {
      this.line2Target.focus();
    }
  }

  // Single-mode pick: write the full label, close, then release focus so the
  // input's own blur->cr-inline-field#patchField fires exactly once.
  #selectSingle(item) {
    const parsed = JSON.parse(item.dataset.address);

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
    // Invalidate any in-flight fetch so it cannot reopen the dropdown post-pick.
    this.generation += 1;

    this.line1Target.value = parsed.label;
    this.hideSuggestions();
    this.line1Target.blur();
  }

  // Single-mode: hide the dropdown when focus leaves the input.
  #hideOnFocusOut() {
    this.hideSuggestions();
  }

  // Single-mode keyboard navigation over the suggestion options.
  #handleKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#moveActiveOption(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#moveActiveOption(-1);
        break;
      case 'Enter': {
        const active = this.#activeOption();
        if (active) {
          event.preventDefault();
          this.#selectSingle(active);
        }
        break;
      }
      case 'Escape':
        // Suggestions ouvertes : Escape les ferme ET est consommé (`preventDefault`),
        // sinon un slide-over non modal qui héberge le champ (le lieu de la prochaine
        // réunion dans le panneau de la revue de l'agent, SEC-1 S14) se fermerait
        // aussi : `slide_over_controller.js` ignore un Escape `defaultPrevented`.
        // Suggestions cachées : Escape n'est pas consommé, le panneau se ferme.
        if (!this.suggestionsTarget.classList.contains('hidden')) {
          event.preventDefault();
        }
        this.hideSuggestions();
        break;
      default:
        break;
    }
  }

  // Move the active-option highlight by delta, wrapping at both ends.
  #moveActiveOption(delta) {
    const options = this.#suggestionOptions();
    if (options.length === 0) return;

    const currentIndex = options.findIndex((option) => option.dataset.active === 'true');
    const nextIndex =
      currentIndex === -1
        ? delta > 0
          ? 0
          : options.length - 1
        : (currentIndex + delta + options.length) % options.length;

    options.forEach((option, index) => this.#setOptionActive(option, index === nextIndex));
  }

  // Toggle the persistent highlight on an option (reuses the hover class).
  #setOptionActive(option, active) {
    if (active) {
      option.dataset.active = 'true';
      option.classList.add('bg-primary-50');
    } else {
      delete option.dataset.active;
      option.classList.remove('bg-primary-50');
    }
  }

  #activeOption() {
    return this.#suggestionOptions().find((option) => option.dataset.active === 'true') || null;
  }

  #suggestionOptions() {
    return Array.from(this.suggestionsTarget.children);
  }

  // Apply highlight effect to fields (green glow)
  highlightFields(fields) {
    fields.forEach((field) => {
      // Add highlight classes
      field.classList.add('border-success-400', 'bg-success-50', 'border-2');
      field.classList.remove('border-neutral-300');

      // Remove highlight after 2.5 seconds
      if (this.highlightTimeout) {
        clearTimeout(this.highlightTimeout);
      }

      this.highlightTimeout = setTimeout(() => {
        fields.forEach((f) => {
          f.classList.remove('border-success-400', 'bg-success-50', 'border-2');
          f.classList.add('border-neutral-300');
        });
      }, 2500);
    });
  }

  // Clear the form
  clear() {
    if (this.hasLine1Target) this.line1Target.value = '';
    if (this.hasLine2Target) this.line2Target.value = '';
    if (this.hasPostalCodeTarget) this.postalCodeTarget.value = '';
    if (this.hasCityTarget) this.cityTarget.value = '';
    if (this.hasCountryTarget) this.countryTarget.value = 'France';
    this.hideSuggestions();
  }
}
